import React, { useState, useEffect } from 'react';
import { Info, Lock, Users, Shield, Crown, Settings } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getRoleDisplayInfo, getPermissionMessage } from '../utils/permissionUtils';
import { api } from '../lib/api';

interface PermissionInfoProps {
  context?: 'students' | 'general';
  className?: string;
}

interface UserAssignments {
  username: string;
  classes: Array<{
    role_name: string;
    assignment_type: string;
    assignment_value: string;
    granted_at: string;
    expires_at?: string;
    is_active: boolean;
    notes?: string;
    granted_by_username?: string;
  }>;
  departments: any[];
  subjects: any[];
  global: any[];
}

/**
 * Komponente zur Anzeige von Berechtigungsinformationen für den aktuellen Benutzer
 */
export default function PermissionInfo({ context = 'general', className = '' }: PermissionInfoProps) {
  const { user } = useAuth();
  const [userAssignments, setUserAssignments] = useState<UserAssignments | null>(null);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  
  // Load user assignments for class teachers
  useEffect(() => {
    if (user?.role === 'klassenlehrer' && context === 'students') {
      setLoadingAssignments(true);
      api.get('/assignments/my-assignments')
        .then(response => {
          if (response.data.success) {
            setUserAssignments(response.data.data);
          }
        })
        .catch(error => {
          console.error('Error loading user assignments:', error);
        })
        .finally(() => {
          setLoadingAssignments(false);
        });
    }
  }, [user?.role, context]);
  
  if (!user?.role) return null;
  
  const roleInfo = getRoleDisplayInfo(user.role);
  const permissionMessage = getPermissionMessage(user.role);
  
  if (!roleInfo) return null;
  
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <Settings className="h-4 w-4" />;
      case 'schulleitung': return <Crown className="h-4 w-4" />;
      case 'abteilungsleiter': return <Shield className="h-4 w-4" />;
      case 'klassenlehrer': return <Users className="h-4 w-4" />;
      case 'teacher': return <Users className="h-4 w-4" />;
      case 'student': return <Users className="h-4 w-4" />;
      default: return <Info className="h-4 w-4" />;
    }
  };
  
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-50 border-red-200 text-red-800';
      case 'schulleitung': return 'bg-purple-50 border-purple-200 text-purple-800';
      case 'abteilungsleiter': return 'bg-indigo-50 border-indigo-200 text-indigo-800';
      case 'klassenlehrer': return 'bg-cyan-50 border-cyan-200 text-cyan-800';
      case 'teacher': return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'student': return 'bg-green-50 border-green-200 text-green-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };
  
  const getContextMessage = () => {
    if (context === 'students') {
      switch (user.role) {
        case 'admin':
        case 'schulleitung':
        case 'abteilungsleiter':
          return 'Sie können alle Schüler bearbeiten und verwalten.';
        case 'klassenlehrer':
          return 'Sie haben klassenbezogene Berechtigungen für die Schülerverwaltung.';
        case 'teacher':
          return 'Sie haben nur Lesezugriff auf Schülerdaten.';
        case 'student':
          return 'Sie können nur Ihre eigenen Daten einsehen.';
        default:
          return permissionMessage;
      }
    }
    return permissionMessage;
  };
  
  return (
    <div className={`rounded-lg border p-3 ${getRoleColor(user.role)} ${className}`}>
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 mt-0.5">
          {getRoleIcon(user.role)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-medium">
              {roleInfo.displayName}
            </h4>
            {user.role === 'klassenlehrer' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 text-cyan-800">
                Klassenspezifisch
              </span>
            )}
          </div>
          <p className="text-xs opacity-90">
            {getContextMessage()}
          </p>
          
          {/* Zusätzliche Informationen für Klassenlehrer */}
          {user.role === 'klassenlehrer' && context === 'students' && (
            <div className="mt-2 pt-2 border-t border-cyan-200/50">
              {loadingAssignments ? (
                <p className="text-xs opacity-75">
                  📋 Lade Ihre zugewiesenen Klassen...
                </p>
              ) : userAssignments?.classes && userAssignments.classes.length > 0 ? (
                <div>
                  <p className="text-xs opacity-75 mb-2">
                    📋 <strong>Ihre zugewiesenen Klassen:</strong>
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {userAssignments.classes.map((assignment, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-cyan-100 text-cyan-800 border border-cyan-200"
                      >
                        {assignment.assignment_value}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs opacity-75 mt-2">
                    💡 <strong>Hinweis:</strong> Sie können nur Schüler aus diesen Klassen bearbeiten.
                  </p>
                </div>
              ) : (
                <p className="text-xs opacity-75">
                  💡 <strong>Hinweis:</strong> Sie haben noch keine spezifischen Klassen zugewiesen bekommen. 
                  Wenden Sie sich an die Schulleitung oder den Administrator.
                </p>
              )}
            </div>
          )}
          
          {/* Zusätzliche Informationen für normale Lehrer */}
          {user.role === 'teacher' && context === 'students' && (
            <div className="mt-2 pt-2 border-t border-blue-200/50">
              <p className="text-xs opacity-75">
                💡 <strong>Hinweis:</strong> Für Änderungen an Schülerdaten wenden Sie sich an 
                den Klassenlehrer, Abteilungsleiter oder die Schulleitung.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
